import { requireClient } from '@/lib/matrix/client'
import { useAuthStore } from '@/lib/store/auth-store'
import type { SerializedEvent } from '@/lib/matrix/events'
import { useTimelineStore, selectTimeline } from '@/lib/store/timeline-store'

interface ReactionBarProps {
  event: SerializedEvent
  roomId: string
}

interface ReactionGroup {
  emoji: string
  count: number
  users: string[]
  myReactionEventId: string | null
}

export function ReactionBar({ event, roomId }: ReactionBarProps) {
  const userId = useAuthStore((s) => s.userId)
  const timeline = useTimelineStore(selectTimeline(roomId))

  const reactionEvents = timeline.filter(
    (e) =>
      e.type === 'm.reaction' &&
      (e.content['m.relates_to'] as Record<string, unknown>)?.event_id === event.eventId
  )

  if (reactionEvents.length === 0) return null

  const groups = new Map<string, ReactionGroup>()
  for (const re of reactionEvents) {
    const rel = re.content['m.relates_to'] as Record<string, unknown> | undefined
    const emoji = (rel?.key as string) ?? ''
    if (!emoji) continue

    const existing = groups.get(emoji) ?? { emoji, count: 0, users: [], myReactionEventId: null }
    existing.count++
    existing.users.push(re.senderName)
    if (re.sender === userId) {
      existing.myReactionEventId = re.eventId
    }
    groups.set(emoji, existing)
  }

  const toggleReaction = async (group: ReactionGroup) => {
    const client = requireClient()
    if (group.myReactionEventId) {
      await client.redactEvent(roomId, group.myReactionEventId)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (client as any).sendEvent(roomId, 'm.reaction', {
        'm.relates_to': {
          rel_type: 'm.annotation',
          event_id: event.eventId,
          key: group.emoji,
        },
      })
    }
  }

  return (
    <div className="reactions">
      {Array.from(groups.values()).map((group) => (
        <button
          key={group.emoji}
          onClick={() => toggleReaction(group)}
          className={`reaction${group.myReactionEventId ? ' reaction-mine' : ''}`}
          title={group.users.join(', ')}
        >
          <span style={{ fontSize: 13 }}>{group.emoji}</span>
          <span className="font-data" style={{ fontSize: 11 }}>{group.count}</span>
        </button>
      ))}
    </div>
  )
}
